import { yupResolver } from '@hookform/resolvers/yup';
import { AorCancellationStatus } from '@zorro/clients';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Stack } from '@zorro/web/design-system';
import { RecordForActions } from '@zorro/web/shared-ui';
import { useBatchCallEndpoint, useForm } from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import * as yup from 'yup';

import { ChangeAorCancellationStatusInput } from '../../ChangeAorCancellationStatusInput/ChangeAorCancellationStatus';
import { FormFooter } from '../../FormFooter';
import { ErrorsTable } from '../Errors/ErrorsTable';

const aorCancellationStatusSchema = yup.object({
  aorCancellationStatus: yup
    .mixed<AorCancellationStatus>()
    .oneOf(
      Object.values(AorCancellationStatus).filter(
        (status) => status !== AorCancellationStatus.NOT_APPLICABLE
      )
    )
    .required(),
});

type AorCancellationStatusFormFields = yup.InferType<
  typeof aorCancellationStatusSchema
>;

type Props = {
  recordsForActions: RecordForActions[];
  onClose: () => void;
  onSuccess: () => void;
};

export const BulkChangeAorCancellationStatusForm = ({
  recordsForActions,
  onClose,
  onSuccess,
}: Props) => {
  const { executeBatchCall, errors } = useBatchCallEndpoint({
    methodName: 'benefitsControllerUpdateAorConfirmedByEmployee',
    singularItemName: 'enrollment record',
    action: 'updated',
    batchSize: 5,
  });

  const {
    control,
    formState: { isValid },
    getValues,
  } = useForm<AorCancellationStatusFormFields>({
    mode: 'all',
    resolver: yupResolver(aorCancellationStatusSchema),
  });

  async function handleSubmit() {
    const { aorCancellationStatus } = getValues();

    const isAorConfirmedByEmployee =
      aorCancellationStatus === AorCancellationStatus.CONFIRMED_BY_EMPLOYEE;

    const batchItems = recordsForActions
      .filter(({ benefitId }) => benefitId)
      .map(({ benefitId }) => {
        return {
          key: benefitId!,
          params: [benefitId!, { isAorConfirmedByEmployee }] as const,
        };
      });

    await executeBatchCall(batchItems, { onSuccess });
  }

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Save"
        primaryButtonProps={{ onClick: handleSubmit, disabled: !isValid }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{
          onClick: onClose,
        }}
      />
    ),
    [handleSubmit, isValid, onClose]
  );
  useDrawerFooter(drawerFooter);

  return (
    <Stack>
      <ChangeAorCancellationStatusInput
        control={control}
        name="aorCancellationStatus"
        selectProps={{ searchable: false }}
      />

      <ErrorsTable
        tableTitle="Enrollment record"
        errors={errors}
        isBulk={recordsForActions.length > 1}
      />
    </Stack>
  );
};
